import { useCallback, useEffect, useState } from 'react'

import Message from 'ageas-react-component-message'
import Section from 'ageas-react-component-section'

import { ENV } from '../../Environment'
import { useAnimalStore } from '../../Stores/animalStore'
import { useAnimalValidateStore } from '../../Stores/useAnimalValidateStore'
import { useGenerateProposalDocumentsStore } from '../../Stores/useGenerateProposalDocumentsStore'
import { useRunPetisSimulationStore } from '../../Stores/useRunPetisSimulationStore'
import { AnimalsSecured, QuestionsBlock } from '../../Types/AnimalStore'
import { OPTIONS_YES_NO } from '../../constants/optionsYesNo'
import { t } from '../../i18n'
import {
  initialValidationQuestionary,
  validateObjectValues,
} from '../../utils/validates'
import { RadioButtonContainer } from '../OwnerData/style'
import { RadioSectionContainer } from '../SummaryConditions/styles'
import { OwnerQuestionary } from './OwnerQuestionary'
import { Radio } from './Radio'
import { QuizContainer } from './styles'

type AnimalQuizProps = {
  disabled?: boolean
  animal: AnimalsSecured
  setIsValid: (value: { [key: string]: boolean }) => void
}

export const AnimalQuiz = ({
  animal,
  setIsValid,
  disabled,
}: AnimalQuizProps) => {
  const { animalsList, updateAnimal } = useAnimalStore()
  const [hasUpdate, setHasUpdate] = useState(true)
  const { answeredList, setAnsweredList } = useAnimalValidateStore()
  const { setEnabledRunPetisRequest } = useRunPetisSimulationStore()
  const { isGenerateProposalDocuments } = useGenerateProposalDocumentsStore()
  const getOwnerSectionCode = (declaration: QuestionsBlock) => {
    let ownerSectionCode

    declaration.questions.forEach((question) => {
      if ((question.conditions || [])?.length > 0) {
        ownerSectionCode = declaration.id
      }
    })

    return ownerSectionCode
  }

  const changeValue = (
    questionId: any,
    id: any,
    value: any,
    isOwnerQuestion = false,
    affectedQuestionCode?: string,
  ) => {
    const currentAnimal = animalsList.filter((item) => item.id === animal.id)[0]

    const currentQuestionary = currentAnimal.questionary || []
    const currentQuestion = currentQuestionary.find(
      (question) => question.id === questionId,
    )

    const questions = currentQuestion?.questions || []

    const newCurrentQuestion = {
      ...currentQuestion,
      questions: questions.map((item) => {
        if (
          isOwnerQuestion &&
          item.id === affectedQuestionCode &&
          value.toLowerCase() === OPTIONS_YES_NO.yes.toLowerCase()
        ) {
          return {
            ...item,
            answer: null,
          }
        } else if (item.id === id) {
          return {
            ...item,
            answer: value,
          }
        }

        return item
      }),
    }

    const questionary = currentQuestionary.map((item) => {
      if (item.id === questionId) {
        return {
          ...item,
          ...newCurrentQuestion,
        }
      }

      return item
    })

    updateAnimal({ questionary }, animal.id)
    setHasUpdate(true)
    setEnabledRunPetisRequest(true)
  }

  const validationQuiz = (value: any) => {
    setAnsweredList(value)
  }

  const checkAnsweredListValidator = useCallback(() => {
    const validate = validateObjectValues(answeredList)
    setIsValid({ [`questionsAnimal${animal.id}`]: validate })
  }, [animal.id, answeredList, setIsValid])

  useEffect(() => {
    if (hasUpdate) {
      checkAnsweredListValidator()
      setHasUpdate(false)
    }
  }, [answeredList, checkAnsweredListValidator, hasUpdate])

  useEffect(() => {
    setAnsweredList(initialValidationQuestionary(animalsList))
  }, [animalsList])

  return (
    <QuizContainer>
      <Message type="warning" text={t('pages.step1.mandatory')} />
      {animal.questionary?.map((declaration, index) => {
        if (declaration.id === getOwnerSectionCode(declaration)) {
          return (
            <OwnerQuestionary
              key={index}
              animalId={animal.id}
              declaration={declaration}
              changeValue={changeValue}
              validationQuiz={validationQuiz}
              disabled={disabled}
            />
          )
        }

        return (
          <RadioSectionContainer key={index}>
            {declaration.title && (
              <Section
                id="healthSection"
                name={declaration.title}
                config={{ labelSize: '20px', labelBold: '500' }}
                colorTheme="blue"
              />
            )}
            <RadioButtonContainer>
              {declaration.questions.map((item) => (
                <>
                  <Radio
                    isMandatory
                    key={item.id}
                    item={item}
                    readOnly={isGenerateProposalDocuments || disabled}
                    changeValue={(value: any) => {
                      if (!isGenerateProposalDocuments) {
                        changeValue(declaration.id, item.id, value)
                        validationQuiz({
                          [`${animal.id}-${declaration.id}-${item.id}`]: value,
                        })
                      }
                    }}
                  />
                  {item.id === ENV().DangerousBreedQuestion &&
                    declaration?.questions?.find(
                      (question) =>
                        question.id === ENV().DangerousBreedQuestion,
                    )?.answer === OPTIONS_YES_NO.yes && (
                      <Message
                        type="warning"
                        text={t('pages.step1.breedDangerousMessage')}
                      />
                    )}
                </>
              ))}
            </RadioButtonContainer>
          </RadioSectionContainer>
        )
      })}
    </QuizContainer>
  )
}
